import { Auth0Client, GenericError } from '@auth0/auth0-spa-js';
import jwtDecode from 'jwt-decode';
import { datadogLogs } from '@datadog/browser-logs';
import { AccessToken, IdToken, User } from '@vooban/tc3-types/common';

const oneSecondInMilliseconds = 1000;

// Auth0 errors that mean the silent session is over and cannot be refreshed: the
// rotating refresh token was revoked/expired/rotated away (invalid_grant), or it is
// gone from the shared cache — e.g. after a logout in another tab (missing_refresh_token,
// a GenericError subclass). Auth0's own docs guard on both. These warrant a clean
// re-login; transient failures (network, timeout, cross-tab lock, rate limit) are NOT
// terminal and must be re-thrown so callers can retry or surface them.
const isTerminalAuthError = (error: unknown): boolean =>
  error instanceof GenericError && (error.error === 'invalid_grant' || error.error === 'missing_refresh_token');

export default class Auth {
  // ---------------------------------------------------------------------------
  // Synchronous readers. UNCHANGED from the implicit-flow implementation.
  //
  // These keep reading the "legacy" localStorage keys (access_token, id_token,
  // expires_at). Those keys are now a MIRROR maintained by syncToLegacyStorage()
  // from the @auth0/auth0-spa-js cache. Every MFE and helper that reads tokens
  // synchronously continues to work without changes.
  // ---------------------------------------------------------------------------

  static getAccessToken = () => window.localStorage.getItem('access_token') || '';

  static getExpiration(): number {
    const expiresAt = localStorage.getItem('expires_at');
    return +(expiresAt || 0);
  }

  static isLoggedIn = (): boolean => {
    const savedExpireAt = window.localStorage.getItem('expires_at');

    if (!savedExpireAt) return false;
    const expiresAt = JSON.parse(savedExpireAt);
    return new Date().getTime() < expiresAt;
  };

  static getGroupSites = (decodedToken: AccessToken) => {
    if (!decodedToken || !decodedToken['https://qsl.com/groupsSites/']) return false;
    return decodedToken['https://qsl.com/groupsSites/'].reduce(
      (acc, { name, sites }) => ({ ...acc, [name]: sites }),
      {}
    );
  };

  static getJurisdictions = (decodedToken: AccessToken) => {
    if (!decodedToken || !decodedToken['https://qsl.com/groupsSites/']) return false;
    return decodedToken['https://qsl.com/groupsSites/'].reduce(
      (acc, { name, jurisdictions }) => (jurisdictions ? { ...acc, [name]: jurisdictions } : acc),
      {}
    );
  };

  static getSiteGroupsByRole = (decodedToken: AccessToken) => {
    if (!decodedToken || !decodedToken['https://qsl.com/groupsSites/']) return false;
    return decodedToken['https://qsl.com/groupsSites/'].reduce(
      (acc, { name, siteGroups }) => (siteGroups ? { ...acc, [name]: siteGroups } : acc),
      {}
    );
  };

  static decodeIdToken = (): IdToken | null => {
    const userToken = window.localStorage.getItem('id_token');
    return userToken ? jwtDecode<IdToken>(userToken) : null;
  };

  static getUserToken = () => {
    const user = Auth.decodeIdToken();
    return user || { sub: 'global' };
  };

  static getUser(): User | null {
    const idToken = Auth.decodeIdToken();
    if (!idToken) return null;

    const accessToken = localStorage.getItem('access_token')!;
    const decodedAccessToken = jwtDecode<AccessToken>(accessToken);

    const contact = decodedAccessToken['http://qsl.com/contacts']?.[0];

    return {
      ...idToken,
      accessToken,
      groups: decodedAccessToken['https://qsl.com/groups/'],
      groupSites: Auth.getGroupSites(decodedAccessToken),
      jurisdictions: Auth.getJurisdictions(decodedAccessToken),
      isInternal: decodedAccessToken['https://qsl.com/qslinternaluser/'],
      contactId: decodedAccessToken['http://qsl.com/contactId'] || contact?.contactId,
      companyId: decodedAccessToken['http://qsl.com/companyId'] || contact?.companyId,
      emailAddress:
        decodedAccessToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ?? idToken.nickname,
      siteGroupsByRole: Auth.getSiteGroupsByRole(decodedAccessToken),
    };
  }

  // ---------------------------------------------------------------------------
  // The legacy-key mirror. These are the ONLY writers of the clean keys.
  // ---------------------------------------------------------------------------

  private static writeLegacyKeys(accessToken: string, idToken: string, expiresAt: number): void {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('id_token', idToken);
    localStorage.setItem('expires_at', JSON.stringify(expiresAt));
  }

  private static clearLegacyKeys(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    // Legacy implicit-flow key; the SDK now owns the refresh token in its own cache.
    localStorage.removeItem('refresh_token');
  }

  private readonly client: Auth0Client;

  // Guards against a burst of concurrent getTokenSilently() rejections each firing
  // its own loginWithRedirect(): only the first one triggers the redirect.
  private redirectingToLogin = false;

  constructor() {
    // Authorization Code + PKCE with rotating refresh tokens.
    // - useRefreshTokens: getTokenSilently() refreshes via the refresh token (no iframe).
    // - cacheLocation 'localstorage': the SDK cache is shared across tabs, so its
    //   Web Locks + shared cache coordinate cross-tab refresh on their own.
    this.client = new Auth0Client({
      domain: String(process.env.REACT_APP_AUTH0_CUSTOMDOMAIN),
      clientId: String(process.env.REACT_APP_AUTH0_CLIENTID),
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        redirect_uri: window.location.origin + '/callback',
        scope: 'openid profile offline_access',
      },
    });
  }

  // Pull the current (refreshing on demand) tokens out of the SDK cache and mirror
  // them into the clean legacy keys. This is the bridge that lets every synchronous
  // reader keep working. Returns the access token.
  syncToLegacyStorage = async (): Promise<string> => {
    const { access_token, id_token, expires_in } = await this.client.getTokenSilently({ detailedResponse: true });
    const expiresAt = expires_in * oneSecondInMilliseconds + Date.now();
    Auth.writeLegacyKeys(access_token, id_token, expiresAt);
    return access_token;
  };

  // Thin pass-through exposed to MFEs via window.qsl.tc3.auth.getTokenSilently, and
  // used by SignalR's accessTokenFactory. The SDK serializes concurrent + cross-tab
  // calls and refreshes on demand. When the silent session has ended (a terminal
  // refresh-token error) we redirect to a clean re-login via redirectToLogin() — the
  // same teardown the startup path uses — instead of letting the rejection bubble to
  // callers as an unhandled RUM error. Transient failures are re-thrown so genuine
  // problems still surface.
  getTokenSilently = async (): Promise<string> => {
    try {
      return await this.syncToLegacyStorage();
    } catch (error) {
      if (!isTerminalAuthError(error)) {
        // Diagnostic breadcrumb: surface the real code/type of any refresh failure we
        // do NOT treat as terminal, so a session-end arriving in an unexpected shape is
        // visible (and the invalid_grant/missing_refresh_token assumption is verifiable
        // against real prod errors) instead of silently re-throwing into an unhandled RUM error.
        datadogLogs.logger.info('getTokenSilently: non-terminal refresh failure re-thrown', {
          errorCode: (error as { error?: string } | null)?.error,
          errorType: error instanceof Error ? error.constructor.name : typeof error,
        });
        throw error;
      }
      // Only the visible tab redirects, so tabs a user left open in the background do not all
      // navigate themselves to login on a shared refresh-token expiry. A hidden tab waits: it
      // keeps its (now stale) key mirror, so its readers are not nulled, and it redirects or
      // recovers on its NEXT getTokenSilently — the next request that tab makes, or a SignalR
      // reconnect — not on focus itself (no visibilitychange trigger is wired here). If another
      // tab re-establishes the session first, that next call just succeeds. redirectingToLogin
      // keeps a burst of concurrent callers in the visible tab down to a single redirect.
      if (document.visibilityState === 'visible' && !this.redirectingToLogin) {
        this.redirectingToLogin = true;
        datadogLogs.logger.info('Silent session ended mid-request; redirecting to login.');
        try {
          await this.redirectToLogin();
        } catch (loginError) {
          // Could not even start the login redirect (e.g. PKCE/crypto or storage
          // failure). Reset the guard so a later call can retry, and surface this
          // genuine failure rather than wedge the singleton.
          this.redirectingToLogin = false;
          throw loginError;
        }
      }
      // Either a redirect is in flight (page navigating away) or this is a hidden tab that
      // is deliberately waiting. Never resolve, so no caller proceeds with an empty bearer —
      // which would 401 and, via SignalR, trigger a logout that races the login redirect.
      return new Promise<string>(() => undefined);
    }
  };

  // Startup bootstrap. Tries to silently establish a session from the stored
  // refresh token and populate the legacy keys. Returns false when there is no
  // valid session (caller should redirect to login).
  ensureSession = async (): Promise<boolean> => {
    try {
      await this.syncToLegacyStorage();
      return true;
    } catch (error) {
      datadogLogs.logger.info(`No silent session available: ${(error as Error)?.message}`);
      Auth.clearLegacyKeys();
      return false;
    }
  };

  login = (): Promise<void> => this.client.loginWithRedirect();

  // Saves the current URL so the user lands back here after re-auth, then starts a fresh
  // Authorization Code + PKCE login. Shared by the startup path (portal.ts) and the
  // mid-session refresh-failure path (getTokenSilently).
  // It deliberately does NOT clear the legacy-key mirror: those keys are shared across tabs
  // via localStorage, so clearing them mid-session would sign out every other open tab (their
  // synchronous tc3-core readers would see null) and, if the redirect itself failed, leave
  // this tab blank with nowhere to go. The mirror is torn down only where the session truly
  // ends — ensureSession() at startup and logout(). This tab navigates away anyway, and
  // handleAuthentication() re-mirrors on return from /callback.
  redirectToLogin = (): Promise<void> => {
    localStorage.setItem('requestedURL', window.location.href);
    return this.login();
  };

  // Exchanges the ?code returned on /callback for tokens (PKCE), then mirrors them.
  handleAuthentication = async (): Promise<void> => {
    await this.client.handleRedirectCallback();
    await this.syncToLegacyStorage();
  };

  logout = async (): Promise<void> => {
    // Revoke the rotating refresh token BEFORE the SDK clears its cache and redirects,
    // so a stolen refresh token cannot mint new tokens after logout (finding 3.1).
    // logout() ends the SSO session and clears the cache but does NOT call /oauth/revoke;
    // revokeRefreshToken() does. A failure must never block logout.
    await this.client.revokeRefreshToken().catch(error => {
      datadogLogs.logger.warn(`Refresh token revoke failed: ${(error as Error)?.message}`);
    });

    Auth.clearLegacyKeys();

    Object.keys(localStorage)
      .filter(key => key.endsWith('-query'))
      .forEach(key => localStorage.removeItem(key));

    // The SDK ends the Auth0 session and clears its own cache (including the
    // rotating refresh token).
    this.client.logout({
      logoutParams: {
        returnTo: process.env.REACT_APP_AUTH0_LOGOUT_RETURN_TO,
      },
    });
  };
}
